import { Flash, Box, Button, Checkbox, Link, Section, unit, Text, toast } from '@acast/decibel';
import { DownloadLightGlyph } from '@acast/decibel-glyphs';
import * as Sentry from '@sentry/browser';
import moment from 'moment';
import T from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import styled, { css, keyframes } from 'styled-components';
import preferences from '../../../../app/preferences';
import {
  fetchAudiograms as fetchAudiogramsAction,
  selectAudiograms,
} from '../../../../ducks/audiograms';
import {
  selectPreferenceValue,
  updatePreference as updatePreferenceAction,
} from '../../../../ducks/user';
import Modal from '../../../../shared/components/modal';
import parseMilliseconds from '../../../../shared/helpers/parse-ms';
import { episodePropType } from '../../../../shared/helpers/resource-prop';
import { List, ListItem } from '../list';
import downloadHeadlinerVideo from './downloadHeadlinerVideo';
import redirectToHeadliner from './redirectToHeadliner';

const shake = keyframes`
  10%, 90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%, 80% {
    transform: translate3d(2px, 0, 0);
  }

  30%, 50%, 70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%, 60% {
    transform: translate3d(4px, 0, 0);
  }
`;

const shakeAnimation = css`
  animation: ${shake} 0.7s ease-in-out both;
  transform: translate3d(0, 0, 0);
`;

const Tos = styled.div`
  will-change: transform;
  display: flex;
  ${props => (props.shakeAnimation ? shakeAnimation : undefined)};
`;

const InformationTitle = styled.dt`
  margin: 0 ${unit(1)} 0 0;
  font-weight: 700;
`;

const InformationDetail = styled.dd`
  margin: 0 ${unit(2)} 0 0;
`;

export class AudiogramBase extends React.PureComponent {
  static propTypes = {
    episode: episodePropType.isRequired,
    episodeDuration: T.number,
    videos: T.arrayOf(
      T.shape({
        id: T.string,
        headlinerTraceId: T.string,
        videoProperties: T.shape({
          projectName: T.string,
          width: T.number,
          height: T.number,
          durationMillis: T.number,
          videoUrl: T.string,
        }),
        created: T.string,
      })
    ).isRequired,
    fetchAudiograms: T.func.isRequired,
    updatePreference: T.func.isRequired,
    isTosChecked: T.bool.isRequired,
  };

  static defaultProps = {
    videos: undefined,
    episodeDuration: 0,
  };

  constructor(props) {
    super(props);

    this.state = {
      isRedirectingUser: false,
      downloadingVideoTraceId: undefined,
      shouldAnimateTos: false,
      shouldShowReloadPageInfo: false,
    };

    this.shouldShowTos = !props.isTosChecked;
  }

  componentDidMount() {
    this.props.fetchAudiograms(this.props.episode.data.id);
  }

  tosCheckboxChangeHandler = event => {
    const isChecked = event.target.checked;
    if (isChecked) {
      this.props.updatePreference({
        name: preferences.HEADLINER_TOS,
        value: isChecked,
      });
    }
  };

  createVideoMouseOverHandler = () => {
    const { isTosChecked } = this.props;

    if (!isTosChecked) {
      this.setState({ shouldAnimateTos: true });
    }
  };

  createVideoMouseOutHandler = () => {
    this.setState({ shouldAnimateTos: false });
  };

  createVideoClickHandler = () => {
    const { isTosChecked } = this.props;
    if (isTosChecked) {
      this.setState({ isRedirectingUser: true });

      redirectToHeadliner(this.props.episode.data.id, this.props.episodeDuration)
        .then(() => {
          this.setState({
            isRedirectingUser: false,
            shouldShowReloadPageInfo: true,
          });
        })
        .catch(error => {
          this.setState({ isRedirectingUser: false });
          toast.danger('An error occured while redirecting you to Headliner.', {
            closable: true,
          });
          Sentry.captureException(error);
        });
      return;
    }

    Modal.present({
      title: 'Create video',
      dialog: 'You need to accept the Terms & Conditions to proceed.',
      actions: Modal.Actions.OK,
    });
  };

  downloadVideoHandler = (episodeId, customTraceId) => () => {
    this.setState({
      downloadingVideoTraceId: customTraceId,
    });
    downloadHeadlinerVideo(episodeId, customTraceId)
      .then(() => {
        this.setState({
          downloadingVideoTraceId: undefined,
        });
      })
      .catch(error => {
        this.setState({
          downloadingVideoTraceId: undefined,
        });
        toast.danger('An error occured while downloading the video from Headliner.', {
          closable: true,
        });
        Sentry.captureException(error);
      });
  };

  reloadPageClickHandler = event => {
    event.preventDefault();
    window.location.reload(false);
  };

  render() {
    // We need to omit fecthAudiograms from the restProps. We spread the
    // restProps on the Section to be able to pass DOM attributes and
    // fetchAudiograms is not a valid DOM attributes so it can't be applied.
    const {
      episode,
      videos,
      fetchAudiograms,
      updatePreference,
      episodeDuration,
      isTosChecked,
      ...restProps
    } = this.props;
    const {
      isRedirectingUser,
      downloadingVideoTraceId,
      shouldAnimateTos,
      shouldShowReloadPageInfo,
    } = this.state;

    return (
      <Section {...restProps}>
        <Text variant="h3">Video</Text>
        <Text>
          Create and publish a stunning audio visualization for your social channels with{' '}
          <Link href="https://www.headliner.app/">Headliner</Link>.
        </Text>

        {videos && videos.length > 0 && (
          <List mb={6}>
            {videos.map(({ id, videoProperties, created }) => (
              <ListItem key={id}>
                <Text as="dl" variant="body2" mb={0} style={{ display: 'flex' }}>
                  <InformationTitle>Created:</InformationTitle>
                  <InformationDetail>{moment(created).format('MMM DD, YYYY')}</InformationDetail>

                  <InformationTitle>Format:</InformationTitle>
                  <InformationDetail>{`${videoProperties.width}x${videoProperties.height}`}</InformationDetail>

                  <InformationTitle>Duration:</InformationTitle>
                  <InformationDetail>
                    {parseMilliseconds(videoProperties.durationMillis)}
                  </InformationDetail>
                </Text>
                <Button
                  data-test-id={`publish-settings__audiogram__download-video-button--${id}`}
                  variant="secondary"
                  size="small"
                  glyph={DownloadLightGlyph}
                  busy={downloadingVideoTraceId === id}
                  onClick={this.downloadVideoHandler(episode.data.id, id)}
                >
                  Download video
                </Button>
              </ListItem>
            ))}
          </List>
        )}

        {shouldShowReloadPageInfo && (
          <Flash intent="primary" data-test-id="publish-settings__audiogram__alert-reload" mb={6}>
            You need to{' '}
            <Link href={window.location.href} onClick={this.reloadPageClickHandler}>
              reload the page
            </Link>{' '}
            to see the new video you have just created.
          </Flash>
        )}

        <Box justifyContent="flex-end" alignItems="center" gap={4}>
          {this.shouldShowTos && (
            <Tos className="publish-settings__button-wrapper" shakeAnimation={shouldAnimateTos}>
              <Checkbox
                id="publish-settings__audiogram__checkbox"
                data-test-id="publish-settings__audiogram__checkbox"
                checked={isTosChecked}
                label={
                  <>
                    I agree to the{' '}
                    <Link
                      href="https://static.sparemin.com/static/doc/201808291932/terms-of-service.html"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms &amp; Conditions
                    </Link>
                    .
                  </>
                }
                defaultValue={isTosChecked}
                onChange={this.tosCheckboxChangeHandler}
              />
            </Tos>
          )}
          <Button
            data-test-id="publish-settings__audiogram__create-video-button"
            variant="primary"
            onMouseOver={this.createVideoMouseOverHandler}
            onMouseOut={this.createVideoMouseOutHandler}
            onClick={this.createVideoClickHandler}
            busy={isRedirectingUser}
          >
            Create video
          </Button>
        </Box>
      </Section>
    );
  }
}

export default connect(
  (state, props) => ({
    videos: selectAudiograms(props.episode.data.id)(state),
    isTosChecked: selectPreferenceValue(preferences.HEADLINER_TOS, false)(state),
  }),
  {
    fetchAudiograms: fetchAudiogramsAction,
    updatePreference: updatePreferenceAction,
  }
)(AudiogramBase);
